import { sortBy, uniq } from 'lodash';
import diff, { Difference } from 'microdiff';
import { useMemo } from 'react';

import { useAllEmployees } from '~/data/employees';
import { useAllOrgUnits } from '~/data/orgunits';
import {
  AuditOperation,
  AuditType,
  useAuditSummary,
} from '~/data/userActivity/useGetAuditSummary';
import {
  UserActivityChangeEventsRow,
  censorUserActivityChangeEventRowDataPoints,
  convertAuditSummaryDataToUserActivityChangeEventsRows,
  getCensorDataProperties,
  getIgnoreDataProperties,
  useActivityLogEventDataTranslation,
} from '~/scenes/PeopleModule/components/EmployeeActions/EmployeeActivityLogDialog/components/EmployeeActivityLog/EmployeeChangeActivityLog/data';
import { resolveDataPointChanges } from '~/scenes/PeopleModule/components/EmployeeActions/EmployeeActivityLogDialog/components/EmployeeActivityLog/EmployeeChangeActivityLog/data/dataPoints';

export type UseEmployeeChangesActivityLogProps = {
  employeeId: number;
  startDate: Date;
  endDate: Date;
};

export const useEmployeeChangesActivityLog = (
  props: UseEmployeeChangesActivityLogProps,
) => {
  const { employeeId, startDate, endDate } = props;

  const { data, isLoading } = useAuditSummary(
    {
      target: employeeId,
      startDate,
      endDate,
    },
    { revalidateOnFocus: false },
  );

  const { getEmployeeById } = useAllEmployees();
  const { getUnitByIdIncludeDeleted } = useAllOrgUnits();
  const { getTranslatedEventData } = useActivityLogEventDataTranslation();

  const rows = useMemo(() => {
    const rows = convertAuditSummaryDataToUserActivityChangeEventsRows(
      data,
      getEmployeeById,
      getUnitByIdIncludeDeleted,
      getTranslatedEventData,
    );

    /**
     *  Filter events
     *  - AuthUser is events of user logging in, filter out for now
     */
    // AuthUser events are user log
    const filteredRows = rows.filter(
      (row) => AuditType.AuthUser !== row.auditType,
    );

    // Sort rows to group by changes on same data
    const sortedRows = sortBy(filteredRows, [
      'targetType',
      'targetSubType',
      'auditType',
      'timestamp',
    ]);

    // Resolve changes
    const changeTrackingCases = uniq(
      sortedRows.map((row) => row.changeTrackingKey),
    );

    return changeTrackingCases
      .map((eventCase): UserActivityChangeEventsRow[] => {
        return sortedRows
          .filter((row) => row.changeTrackingKey === eventCase)
          .map((row, index, eventCaseRows): UserActivityChangeEventsRow => {
            const hasDataPointChanges = row.dataPointChanges.length > 0;

            if (
              index > 0 &&
              row.operation === AuditOperation.Update &&
              !hasDataPointChanges
            ) {
              const previousRow = eventCaseRows[index - 1];

              return {
                ...row,
                dataPointChanges: resolveDataPointChanges(
                  previousRow.dataPoints,
                  row.dataPoints,
                ),
              };
            }

            return row;
          });
      })
      .flat()
      .map(censorUserActivityChangeEventRowDataPoints);
  }, [
    data,
    getEmployeeById,
    getUnitByIdIncludeDeleted,
    getTranslatedEventData,
  ]);

  return {
    rows,
    isLoading,
  };
};
